import { PlacedPallet, TruckSpecs } from '../types';

export const SNAP_CONFIG = {
  gridSize: 0.1,      // 10cm grid
  magnetDistance: 0.25, // Distance to snap to other pallets
  wallDistance: 0.15, // Distance to snap to walls
};

/**
 * Calculates effective width/length based on rotation (radians)
 */
export const getEffectiveDimensions = (width: number, length: number, rotationRad: number) => {
  // Normalize rotation to 0, 90, 180, 270 degrees equivalent
  // We assume rotation is always a multiple of Math.PI/2
  const deg = Math.round(rotationRad * (180 / Math.PI)) % 180;
  
  // If rotated 90 or 270 degrees (approx), swap dimensions
  // allowing a small epsilon for float errors, though we use discrete steps
  const isRotated = Math.abs(deg - 90) < 1; 
  
  return {
    width: isRotated ? length : width,
    length: isRotated ? width : length,
  };
};

/**
 * Checks if two rectangles overlap
 */
const rectsOverlap = (
  minX1: number, maxX1: number, minZ1: number, maxZ1: number,
  minX2: number, maxX2: number, minZ2: number, maxZ2: number
) => {
  return !(maxX1 <= minX2 || maxX2 <= minX1 || maxZ1 <= minZ2 || maxZ2 <= minZ1);
};

export const calculateSnapPosition = (
    currentX: number,
    currentZ: number,
    pallet: PlacedPallet,
    allPallets: PlacedPallet[],
    truckSpecs: TruckSpecs
): { x: number, z: number } => {
    let bestX = currentX;
    let bestZ = currentZ;

    // 1. SNAP TO GRID
    // Round to nearest gridSize
    bestX = Math.round(bestX / SNAP_CONFIG.gridSize) * SNAP_CONFIG.gridSize;
    bestZ = Math.round(bestZ / SNAP_CONFIG.gridSize) * SNAP_CONFIG.gridSize;

    // Get my effective dimensions
    const myDims = getEffectiveDimensions(pallet.width, pallet.length, pallet.rotation);
    const myHalfW = myDims.width / 2;
    const myHalfL = myDims.length / 2;

    let minDistX = SNAP_CONFIG.magnetDistance;
    let minDistZ = SNAP_CONFIG.magnetDistance;

    // 2. SNAP TO WALLS
    const leftWall = -truckSpecs.width / 2;
    const rightWall = truckSpecs.width / 2;
    const frontWall = -truckSpecs.length / 2;
    const backWall = truckSpecs.length / 2;

    // Snap X Walls
    if (Math.abs((bestX - myHalfW) - leftWall) < SNAP_CONFIG.wallDistance) {
        bestX = leftWall + myHalfW;
    } else if (Math.abs((bestX + myHalfW) - rightWall) < SNAP_CONFIG.wallDistance) {
        bestX = rightWall - myHalfW;
    }

    // Snap Z Walls
    if (Math.abs((bestZ - myHalfL) - frontWall) < SNAP_CONFIG.wallDistance) {
        bestZ = frontWall + myHalfL;
    } else if (Math.abs((bestZ + myHalfL) - backWall) < SNAP_CONFIG.wallDistance) {
        bestZ = backWall - myHalfL;
    }

    // 3. SNAP TO PALLETS
    // We only snap if we are "close enough" to an edge AND we overlap in the other dimension
    
    for (const other of allPallets) {
        if (other.id === pallet.id) continue;

        const otherDims = getEffectiveDimensions(other.width, other.length, other.rotation);
        const otherHalfW = otherDims.width / 2;
        const otherHalfL = otherDims.length / 2;

        const otherLeft = other.x - otherHalfW;
        const otherRight = other.x + otherHalfW;
        const otherFront = other.z - otherHalfL; // Front of truck is -Z
        const otherBack = other.z + otherHalfL;

        const myLeft = bestX - myHalfW;
        const myRight = bestX + myHalfW;
        const myFront = bestZ - myHalfL;
        const myBack = bestZ + myHalfL;

        // --- Snap X (Stick to Left/Right of neighbor) ---
        // Check Z overlap first
        if (rectsOverlap(0, 1, myFront, myBack, 0, 1, otherFront, otherBack)) {
             // Snap my Left to other Right
             if (Math.abs(myLeft - otherRight) < minDistX) {
                 bestX = otherRight + myHalfW;
                 minDistX = Math.abs(myLeft - otherRight);
             }
             // Snap my Right to other Left
             if (Math.abs(myRight - otherLeft) < minDistX) {
                 bestX = otherLeft - myHalfW;
                 minDistX = Math.abs(myRight - otherLeft);
             }
        }

        // --- Snap Z (Stick to Front/Back of neighbor) ---
        // Check X overlap first
        if (rectsOverlap(myLeft, myRight, 0, 1, otherLeft, otherRight, 0, 1)) {
            // Snap my Front to other Back
            if (Math.abs(myFront - otherBack) < minDistZ) {
                bestZ = otherBack + myHalfL;
                minDistZ = Math.abs(myFront - otherBack);
            }
            // Snap my Back to other Front
            if (Math.abs(myBack - otherFront) < minDistZ) {
                bestZ = otherFront - myHalfL;
                minDistZ = Math.abs(myBack - otherFront);
            }
        }
    }

    return { x: bestX, z: bestZ };
};